import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Stamp as StampIcon } from "lucide-react";
import { api } from "@/lib/api";
import { C, EASE, useContent } from "./theme";
import { SectionHead, splitHeading } from "./Chrome";
import { playThud } from "./sound";

const INKS = [C.rose, C.burgundy, "#9C7A3C", C.rich, C.muted];
const SHAPES = [
  { id: "circle", label: "Round" },
  { id: "rect", label: "Square" },
  { id: "oval", label: "Oval" },
];

function VisitorStamp({ s, i, fresh }) {
  const ink = INKS[i % INKS.length];
  const dims = s.shape === "circle" ? "w-[112px] h-[112px] rounded-full" : s.shape === "oval" ? "w-[140px] h-[96px] rounded-[50%]" : "w-[136px] h-[88px] rounded-md";
  const date = s.created_at ? new Date(s.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "";
  return (
    <motion.div
      layout
      data-testid={`guest-stamp-${i}`}
      initial={fresh ? { scale: 2.4, opacity: 0, rotate: (s.rot || 0) - 25 } : { opacity: 0, y: 10 }}
      whileInView={{ scale: 1, opacity: 0.9, rotate: s.rot || 0, y: 0 }}
      viewport={{ once: true }}
      transition={fresh ? { type: "spring", stiffness: 300, damping: 16 } : { duration: 0.6, delay: (i % 8) * 0.06, ease: EASE }}
      className={`${dims} flex flex-col items-center justify-center text-center px-3 flex-shrink-0`}
      style={{ color: ink, border: `2px solid ${ink}`, boxShadow: `inset 0 0 0 4px ${C.warm}, inset 0 0 0 5px ${ink}`, mixBlendMode: "multiply" }}
    >
      <span className="ed-mono text-[7px] tracking-[0.25em] uppercase">Visited</span>
      <span className="ed-serif italic text-base leading-tight my-0.5 break-words max-w-full">{s.company}</span>
      <span className="ed-mono text-[7px] tracking-[0.2em] uppercase truncate max-w-full">{s.name ? `${s.name} · ` : ""}{date}</span>
    </motion.div>
  );
}

export function GuestbookSection() {
  const c = useContent("guestbook");
  const [stamps, setStamps] = useState([]);
  const [freshId, setFreshId] = useState(null);
  const [form, setForm] = useState({ company: "", name: "", shape: "circle" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(api("/api/guestbook")).then((r) => setStamps(r.data)).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.company.trim()) return toast.error("Add your company name first.");
    setBusy(true);
    try {
      const { data } = await axios.post(api("/api/guestbook"), form);
      playThud();
      setFreshId(data.id);
      setStamps((s) => [data, ...s]);
      setForm({ company: "", name: "", shape: form.shape });
      toast.success("Stamped. Thanks for visiting!");
    } catch {
      toast.error("Couldn't stamp — try again.");
    } finally {
      setBusy(false);
    }
  };
  const [head, ital] = splitHeading(c.heading, "Stamp my", "passport.");
  return (
    <section id="guestbook" data-testid="guestbook-section" className="relative min-h-screen flex flex-col justify-center py-24 md:py-28" style={{ background: C.cream }}>
      <div className="max-w-[1440px] w-full mx-auto px-6 md:px-12">
        <SectionHead num="06" eyebrow={c.eyebrow || "Guestbook · Visitor stamps"} heading={head} italic={ital} aside={c.blurb || "Recruiter, founder, fellow strategist? Leave your company's stamp on my visa page — proof you stopped by."} />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <form onSubmit={submit} data-testid="guestbook-form" className="lg:col-span-4 self-start p-7 md:p-9 relative" style={{ background: C.warm, border: `1px solid ${C.burgundy}1a`, boxShadow: `0 24px 50px ${C.wine}14` }}>
            <span className="absolute inset-2.5 pointer-events-none" style={{ border: `1px solid ${C.gold}66` }} />
            <p className="ed-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: C.gold }}>Immigration desk</p>
            <p className="ed-serif text-3xl font-light mt-2 mb-7" style={{ color: C.wine }}>Your <em style={{ color: C.rose }}>stamp</em></p>
            <label className="block mb-5">
              <span className="ed-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: C.muted }}>Company *</span>
              <input data-testid="guest-company" maxLength={40} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Ogilvy" className="w-full bg-transparent py-2 ed-serif text-xl outline-none border-b focus:border-[#9E3B4B] placeholder:italic placeholder:text-[#9E586280]" style={{ color: C.wine, borderColor: `${C.burgundy}33` }} />
            </label>
            <label className="block mb-5">
              <span className="ed-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: C.muted }}>Your name (optional)</span>
              <input data-testid="guest-name" maxLength={60} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ada" className="w-full bg-transparent py-2 ed-serif text-xl outline-none border-b focus:border-[#9E3B4B] placeholder:italic placeholder:text-[#9E586280]" style={{ color: C.wine, borderColor: `${C.burgundy}33` }} />
            </label>
            <div className="flex gap-2 mb-7">
              {SHAPES.map((s) => (
                <button type="button" key={s.id} data-testid={`guest-shape-${s.id}`} onClick={() => setForm({ ...form, shape: s.id })} className="flex-1 py-2 ed-mono text-[9px] uppercase tracking-[0.2em] rounded-full transition-colors duration-300" style={form.shape === s.id ? { background: C.burgundy, color: C.cream } : { border: `1px solid ${C.burgundy}33`, color: C.wine }}>
                  {s.label}
                </button>
              ))}
            </div>
            <motion.button whileTap={{ scale: 0.94 }} type="submit" disabled={busy} data-testid="guest-submit" className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-full ed-mono text-[11px] uppercase tracking-[0.3em] transition-colors duration-500 hover:bg-[#9E3B4B]" style={{ background: C.burgundy, color: C.cream }}>
              <StampIcon size={14} /> {busy ? "Stamping…" : "Stamp it"}
            </motion.button>
          </form>
          <div className="lg:col-span-8 relative min-h-[360px] p-6 md:p-10" data-testid="guestbook-page" style={{ background: `repeating-linear-gradient(0deg, transparent 0 22px, ${C.blush}66 22px 23px), ${C.warm}`, border: `1px solid ${C.burgundy}1a`, boxShadow: `0 24px 50px ${C.wine}14` }}>
            <div className="flex justify-between mb-6">
              <p className="ed-mono text-[9px] uppercase tracking-[0.35em]" style={{ color: C.gold }}>Visas · Entries</p>
              <p className="ed-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: C.muted }} data-testid="guest-count">{stamps.length} visitors</p>
            </div>
            {stamps.length === 0 && <p className="ed-serif italic text-2xl text-center mt-20" style={{ color: C.muted }}>No stamps yet — be the first.</p>}
            <div className="flex flex-wrap gap-4 md:gap-6 justify-center">
              <AnimatePresence>{stamps.map((s, i) => <VisitorStamp key={s.id} s={s} i={i} fresh={s.id === freshId} />)}</AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
